/**
 * AddressAutocomplete — debounced free-text address input that calls
 * /api/geocode/search (Nominatim/OpenStreetMap proxy) and surfaces a
 * dropdown of pickable matches. On select, fires onSelect({address, lat, lng, ...}).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, MapPin } from 'lucide-react';
import apiClient from '../api/client';

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing an address...',
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  // Debounced query
  useEffect(() => {
    if (!value || value.length < 4) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/api/geocode/search`, { params: { q: value } });
        setResults(res.data || []);
        setOpen(true);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pick = (r) => {
    setOpen(false);
    setResults([]);
    onSelect({
      address: r.display,
      house_number: r.address?.house_number || '',
      road: r.address?.road || '',
      city: r.address?.city || '',
      state: r.address?.state || '',
      postcode: r.address?.postcode || '',
      lat: r.lat,
      lng: r.lng,
    });
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid={testid || 'address-autocomplete'}>
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-9 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-lg">
          {results.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => pick(r)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 flex items-start gap-2"
                data-testid={`address-suggestion-${i}`}
              >
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-sky-500" />
                <span className="truncate">{r.display}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
